/**
 * Discover's search in the URL (jorigin/brewlytics#2290):
 *
 *   #/dashboard/discover?q=berth&tag=port-ops&tag=ais|vessel&-tag=draft
 *     &author=me&updated=30d&created=>=2026-01-01&pick=1&shared=me
 *     &uses=<id>+<id>&uses=<id>&submodel=<model id>&sort=updated
 *
 * Each parameter carries a token's value exactly as it is typed (`tag=a|b` is
 * `tag:a|b`), so the URL, the box and the panel share one grammar. `uses` and
 * `submodel` (#2299) are one parameter per alternative, its ids joined by `+`
 * (`uses=A+B&uses=C` is `uses:A+B|C`); a link from before #2299 has at most
 * one `uses`, one id, which reads as before. Ids, never
 * names: a link means the same search whoever opens it. `author=me` stays
 * `me` in the address bar (the bookmark is "my models"); a link copied to
 * send replaces it with the sender's profile id (`searchLink`), so the
 * recipient sees what the sender meant.
 */
import { QUERY_ID_PATTERN } from '@/types/modelQuery';
import { formatDateValue, quote, quoteRef, type SearchQuery } from './searchSyntax';

/** The query as URL parameters. */
export function queryToParams(q: SearchQuery, options: { meId?: string } = {}): URLSearchParams {
  const p = new URLSearchParams();
  if (q.text.trim()) p.set('q', q.text.trim());
  for (const t of q.tags.all) p.append('tag', t);
  if (q.tags.any.length) p.append('tag', q.tags.any.join('|'));
  for (const t of q.tags.none) p.append('-tag', t);
  for (const a of q.authors) p.append('author', a === 'me' && options.meId ? options.meId : a);
  if (q.updated) p.set('updated', formatDateValue(q.updated));
  if (q.created) p.set('created', formatDateValue(q.created));
  if (q.editorsPick) p.set('pick', '1');
  if (q.shared) p.set('shared', q.shared);
  for (const g of q.functionals) p.append('uses', g.join('+'));
  for (const g of q.submodels) p.append('submodel', g.join('+'));
  if (q.sort) p.set('sort', q.sort);
  return p;
}

/** The query as a location `search` string: `?…`, or '' for none. */
export function queryToSearch(q: SearchQuery, options: { meId?: string } = {}): string {
  const s = queryToParams(q, options).toString();
  return s ? `?${s}` : '';
}

/**
 * One alternative's parameter as names joined by `+`. A `+` typed into an
 * address bar arrives as a space, so ids separated by spaces are joined too.
 */
function groupValue(v: string): string {
  const spaced = v.trim().split(/\s+/);
  const parts = spaced.length > 1 && spaced.every((p) => QUERY_ID_PATTERN.test(p)) ? spaced : v.split('+');
  return parts.map((p) => quoteRef(p.trim())).join('+');
}

/** A value as a token's value: tag groups keep their pipes, each part quoted as needed. */
function pipedValue(v: string): string {
  return v.split('|').map((part) => quote(part.trim())).join('|');
}

/**
 * URL parameters as the box's text. Values are not checked here: the text is
 * parsed like anything typed, so a bad value shows as an invalid token.
 */
export function paramsToText(params: URLSearchParams): string {
  const parts: string[] = [];
  const q = params.get('q');
  if (q?.trim()) parts.push(q.trim());
  for (const t of params.getAll('tag')) if (t.trim()) parts.push(`tag:${pipedValue(t)}`);
  for (const t of params.getAll('-tag')) if (t.trim()) parts.push(`-tag:${quote(t.trim())}`);
  for (const a of params.getAll('author')) if (a.trim()) parts.push(`author:${quote(a.trim())}`);
  const updated = params.get('updated');
  if (updated) parts.push(`updated:${quote(updated)}`);
  const created = params.get('created');
  if (created) parts.push(`created:${quote(created)}`);
  if (params.get('pick') === '1' || params.get('pick') === 'true') parts.push('is:pick');
  const shared = params.get('shared');
  if (shared) parts.push(shared === 'me' ? 'is:shared' : `group:${quote(shared)}`);
  for (const key of ['uses', 'submodel']) {
    const groups = params.getAll(key).filter((g) => g.trim());
    if (groups.length) parts.push(`${key}:${groups.map(groupValue).join('|')}`);
  }
  const sort = params.get('sort');
  if (sort) parts.push(`sort:${quote(sort)}`);
  if (!parts.length) return '';
  // A trailing space settles the last filter: it is in force, not being typed.
  const textParts = q?.trim() ? 1 : 0;
  return parts.length > textParts ? `${parts.join(' ')} ` : parts.join(' ');
}

/** The route the search lives on. */
export const DISCOVER_PATH = '/dashboard/discover';

/**
 * A link to send: the full URL (HashRouter: the route lives after `#`) with
 * `me` replaced by the sender's profile id.
 */
export function searchLink(q: SearchQuery, meId: string | undefined, loc: Pick<Location, 'origin' | 'pathname'> = window.location): string {
  return `${loc.origin}${loc.pathname}#${DISCOVER_PATH}${queryToSearch(q, { meId })}`;
}
